import { useEffect, useState } from "react";

/**
 * Mobile-first reading progress bar shown along the very top of the page.
 *
 * - Hidden on xl+ screens (desktop uses SectionRail instead)
 * - Renders as a thin horizontal bar that fills as the user scrolls the page
 * - Optional segment markers for the same sections shown in SectionRail give
 *   users an instant sense of where they are on the page
 */
export default function ReadingProgress({ sections = [] }) {
  const [pct, setPct] = useState(0);
  const [segments, setSegments] = useState([]); // [{ id, label, pct }]

  useEffect(() => {
    const computeSegments = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      if (total <= 0) { setSegments([]); return; }
      const items = [];
      for (const s of sections) {
        const el = document.querySelector(`[data-testid="${s.id}"]`);
        if (!el) continue;
        const top = el.getBoundingClientRect().top + window.scrollY;
        items.push({ id: s.id, label: s.label, pct: Math.min(99, Math.max(0, (top / total) * 100)) });
      }
      setSegments(items);
    };

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const total = doc.scrollHeight - doc.clientHeight;
      setPct(total > 0 ? Math.min(100, (scrollTop / total) * 100) : 0);
    };

    // Defer segment computation slightly so layout/images settle
    const t = setTimeout(computeSegments, 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", computeSegments);
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", computeSegments);
    };
  }, [sections]);

  return (
    <div
      aria-hidden="true"
      data-testid="reading-progress"
      className="xl:hidden fixed top-0 left-0 right-0 h-[3px] z-40 bg-zinc-100/80 backdrop-blur-sm pointer-events-none"
    >
      <div
        className="h-full bg-gradient-to-r from-[#1a73e8] via-[#9333ea] to-[#ea4335] transition-[width] duration-150 ease-out"
        style={{ width: `${pct}%` }}
        data-testid="reading-progress-fill"
      />
      {/* Section markers */}
      {segments.map((s) => (
        <span
          key={s.id}
          data-testid={`reading-progress-marker-${s.id}`}
          className={`absolute top-0 bottom-0 w-px ${pct >= s.pct ? "bg-white/60" : "bg-zinc-300"}`}
          style={{ left: `${s.pct}%` }}
        />
      ))}
    </div>
  );
}
